
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { WeeklySummary, MeetingNote, ChatMessage } from '../types';
import { askQuestionAboutContext } from '../services/geminiService';

interface Props {
  summary: WeeklySummary;
  notes: MeetingNote[];
}

const FloatingChat: React.FC<Props> = ({ summary, notes }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [query, setQuery] = useState('');
  const [isQuerying, setIsQuerying] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [chatMessages, isQuerying, isOpen]);

  const handleQuery = async (text: string) => {
    if (!text.trim() || isQuerying) return;
    
    const userMsg: ChatMessage = { role: 'user', text, timestamp: Date.now() };
    setChatMessages(prev => [...prev, userMsg]);
    setQuery('');
    setIsQuerying(true);

    try {
      const answer = await askQuestionAboutContext(text, notes, chatMessages);
      const modelMsg: ChatMessage = { role: 'model', text: answer, timestamp: Date.now() };
      setChatMessages(prev => [...prev, modelMsg]);
    } catch (err) {
      console.error("Query Error:", err);
    } finally {
      setIsQuerying(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-8 right-8 z-[100] w-16 h-16 bg-indigo-600 text-white rounded-full shadow-2xl shadow-indigo-500/50 flex items-center justify-center hover:bg-indigo-700 transition-all transform hover:scale-110 active:scale-90 group"
        aria-label="Intelligence Engine Chat"
      >
        {isOpen ? (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <div className="relative">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-300 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-400"></span>
            </span>
          </div>
        )}
      </button>

      {/* Chat Window Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-[90] flex items-end justify-end p-0 sm:p-4 pointer-events-none">
          <div 
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm pointer-events-auto transition-opacity duration-300"
            onClick={() => setIsOpen(false)}
          />
          
          <section className="relative w-full sm:w-1/2 h-full sm:h-[85vh] bg-slate-900 sm:rounded-3xl shadow-2xl pointer-events-auto flex flex-col border border-slate-800 animate-in slide-in-from-right-10 duration-300 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
            
            {/* Header */}
            <div className="p-6 border-b border-slate-800 flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-3">
                <div className="bg-indigo-500/20 p-2 rounded-xl">
                  <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white tracking-tight">Intelligence Engine</h3>
                  <div className="flex items-center space-x-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Workspace Context Active</p>
                  </div>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-slate-500 hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-6 relative z-10">
              {chatMessages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4 opacity-40">
                  <div className="w-20 h-20 bg-slate-800 rounded-3xl flex items-center justify-center mb-6 border border-slate-700">
                    <svg className="w-10 h-10 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <h4 className="text-white font-bold mb-2">Workspace Interrogator</h4>
                  <p className="text-xs max-w-[250px] leading-relaxed">I've parsed {notes.length} items. Ask me for specific details, dates, or decisions mentioned in your meetings.</p>
                </div>
              ) : (
                chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
                    <div className={`max-w-[92%] rounded-2xl px-5 py-4 ${
                      msg.role === 'user' 
                        ? 'bg-indigo-600 text-white rounded-tr-none shadow-lg' 
                        : 'bg-slate-800 text-slate-200 rounded-tl-none border border-slate-700 shadow-xl'
                    }`}>
                      {msg.role === 'user' ? (
                          <p className="text-sm font-medium whitespace-pre-wrap">{msg.text}</p>
                      ) : (
                          <div className="text-[13px] leading-relaxed prose prose-invert prose-sm max-w-none">
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
                                ul: ({ children }) => <ul className="list-disc pl-4 mb-3 space-y-1">{children}</ul>,
                                ol: ({ children }) => <ol className="list-decimal pl-4 mb-3 space-y-1">{children}</ol>,
                                li: ({ children }) => <li className="text-slate-300">{children}</li>,
                                strong: ({ children }) => <strong className="text-indigo-400 font-bold">{children}</strong>,
                                code: ({ children }) => <code className="bg-slate-700 px-1 rounded text-indigo-300">{children}</code>,
                              }}
                            >
                              {msg.text}
                            </ReactMarkdown>
                          </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              {isQuerying && (
                <div className="flex justify-start">
                  <div className="bg-slate-800 rounded-2xl rounded-tl-none px-5 py-4 flex space-x-1.5 items-center border border-slate-700">
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Footer / Input */}
            <div className="p-6 border-t border-slate-800 bg-slate-900/80 backdrop-blur relative z-10">
              {summary.suggestedFollowUps && summary.suggestedFollowUps.length > 0 && chatMessages.length === 0 && (
                <div className="mb-4 flex flex-col gap-2">
                  {summary.suggestedFollowUps.slice(0, 3).map((q, i) => (
                    <button 
                      key={i}
                      onClick={() => handleQuery(q)}
                      className="w-full bg-slate-800 hover:bg-slate-700 text-indigo-300 text-[10px] font-bold px-4 py-3 rounded-xl border border-slate-700 transition-all text-left animate-in slide-in-from-right-2 duration-300"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="line-clamp-2">{q}</span>
                        <svg className="w-3 h-3 flex-shrink-0 ml-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              <div className="relative group">
                <input 
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleQuery(query)}
                  placeholder="Ask intelligence about workspace..."
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-2xl px-5 py-4 pr-14 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all placeholder:text-slate-600 text-sm"
                />
                <button 
                  onClick={() => handleQuery(query)}
                  disabled={isQuerying || !query.trim()}
                  className="absolute right-2 top-2 bottom-2 bg-indigo-600 hover:bg-indigo-500 text-white px-3.5 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default FloatingChat;
